import React from 'react'

import type { Element as HastElement } from 'hast'

const extractText = (node: React.ReactNode): string => {
  if (node == null || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node)
  }
  if (Array.isArray(node)) return node.map(extractText).join('|')
  if (React.isValidElement(node)) {
    return extractText((node.props as { children?: React.ReactNode }).children)
  }
  return ''
}

const getStableTextHash = (text: string): string => {
  let hash = 0
  for (let i = 0; i < text.length; i += 1) {
    hash = Math.imul(31, hash) + text.charCodeAt(i)
  }
  const unsignedHash = hash < 0 ? hash + 0x100000000 : hash
  return unsignedHash.toString(36)
}

/**
 * Build a stable table identity from the rendered table text.
 * Repeated partial tables often share the same headers and local markdown
 * position, so row content must be part of the expansion key.
 */
export const getTableContentSignature = (children: React.ReactNode): string => {
  return `table:${getStableTextHash(extractText(children))}`
}

const getNodePositionSignature = (node?: HastElement): string | null => {
  const start = node?.position?.start
  if (!start) return null

  if (typeof start.offset === 'number') {
    return `offset:${start.offset}`
  }

  return `line:${start.line}:column:${start.column}`
}

/**
 * Use the markdown node position when available so identical tables do not
 * share dialog state. The content signature remains part of the key because
 * positions are local to each ReactMarkdown render block.
 */
export const getMarkdownTableKey = ({
  children,
  node,
}: {
  children: React.ReactNode
  node?: HastElement
}): string => {
  const contentSignature = getTableContentSignature(children)
  const positionSignature = getNodePositionSignature(node)

  return positionSignature
    ? `${contentSignature}:${positionSignature}`
    : contentSignature
}

/**
 * Scope the table key to the current assistant message so identical tables in
 * different messages do not share the same expanded dialog state.
 */
export const getExpandedMarkdownTableKey = ({
  messageId,
  children,
  node,
}: {
  messageId: string
  children: React.ReactNode
  node?: HastElement
}): string => {
  return `${messageId}:${getMarkdownTableKey({ children, node })}`
}
